import React from "react"
import L10n from "lib/carrot-l10n"
import { FormProvider, useForm } from "react-hook-form"
import { Container, H2 } from "@carrotfertility/carotene"
import { Steps } from "../workflow/steps"
import { useCurrentUser } from "../../context/user/UserContext"
import { useStateMachine } from "../../context/stateMachine/StateMachineContext"
import useSubmitWithErrorCatch from "../hooks/useSubmitWithErrorCatch"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
} from "../shared"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const FieldName = "memberCoverageTypeSelectValue"
const ViewName = Steps.MEMBER_COVERAGE_TYPE

export const enum MemberCoverageTypeOptions {
  MEMBER_ONLY = "MEMBER_ONLY",
  MEMBER_AND_PARTNER = "MEMBER_AND_PARTNER",
  MEMBER_PARTNER_DEPENDENTS = "MEMBER_PARTNER_DEPENDENTS",
  MEMBER_DEPENDENTS_NOT_PARTNER = "MEMBER_DEPENDENTS_NOT_PARTNER",
  IM_NOT_SURE = "IM_NOT_SURE"
}

const memberCoverageTypeIdToLabel = {
  [MemberCoverageTypeOptions.MEMBER_ONLY]: `Just me`,
  [MemberCoverageTypeOptions.MEMBER_AND_PARTNER]: `Me and a partner`,
  [MemberCoverageTypeOptions.MEMBER_PARTNER_DEPENDENTS]: `Me and a partner and dependents`,
  [MemberCoverageTypeOptions.MEMBER_DEPENDENTS_NOT_PARTNER]: `Me and dependents but no partner`,
  [MemberCoverageTypeOptions.IM_NOT_SURE]: `I'm not sure`
}

export const getMemberCoverageLabelFromId = (id: MemberCoverageTypeOptions): string => memberCoverageTypeIdToLabel[id]

function MemberCoverageTypeStep(): JSX.Element {
  const { updateUserInfo, memberCoverageTypeSelectValue } = useCurrentUser()
  const { setNextStep } = useStateMachine(ViewName, memberCoverageTypeSelectValue)
  const formMethods = useForm()
  const { onSubmit } = useSubmitWithErrorCatch(async (args) => {
    const val = args[FieldName].id
    val !== "IM_NOT_SURE" &&
      (await updateUserInfo({
        memberCoverageTypeSelectValue: val
      }))
    setNextStep(ViewName, val)
  })

  const { handleSubmit } = formMethods

  return (
    <FormProvider {...formMethods}>
      <Container padding="none" stack="huge">
        <BenefitEnrollmentFlowModalBackButton />
      </Container>
      <H2
        stack="large"
        variant="primary"
      >{l`Does your health plan cover just you or does it cover other people as well?`}</H2>
      <Container padding="none" stack="giant">
        <BenefitEnrollmentModalFormSelect
          name={FieldName}
          defaultValue={memberCoverageTypeSelectValue}
          options={[
            {
              label: getMemberCoverageLabelFromId(MemberCoverageTypeOptions.MEMBER_ONLY),
              id: MemberCoverageTypeOptions.MEMBER_ONLY
            },
            {
              label: getMemberCoverageLabelFromId(MemberCoverageTypeOptions.MEMBER_AND_PARTNER),
              id: MemberCoverageTypeOptions.MEMBER_AND_PARTNER
            },
            {
              label: getMemberCoverageLabelFromId(MemberCoverageTypeOptions.MEMBER_PARTNER_DEPENDENTS),
              id: MemberCoverageTypeOptions.MEMBER_PARTNER_DEPENDENTS
            },
            {
              label: getMemberCoverageLabelFromId(MemberCoverageTypeOptions.MEMBER_DEPENDENTS_NOT_PARTNER),
              id: MemberCoverageTypeOptions.MEMBER_DEPENDENTS_NOT_PARTNER
            },
            {
              label: getMemberCoverageLabelFromId(MemberCoverageTypeOptions.IM_NOT_SURE),
              id: MemberCoverageTypeOptions.IM_NOT_SURE
            }
          ]}
        />
      </Container>
      <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
    </FormProvider>
  )
}

export { MemberCoverageTypeStep }
